import React from "react";
import { ChevronRight, Star, Utensils, Hotel } from "lucide-react";
import { TripProgressBar, useTripProgress } from "../TripProgressBar";
import { EmergencyBlock } from "../EmergencyBlock";
import { PhotoSpotCard } from "../PhotoSpotCard";
import { LockedDays } from "../LockedDays";

export function ItineraryTab({ result, trip, onUnlock }) {
  // Always call hooks at top level — before any conditional returns
  const { currentDayIndex } = useTripProgress(trip?.date_depart, trip?.date_retour);

  if (!result) return (
    <div className="text-[#9A8F86] py-10 text-center italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>
      Itinéraire non disponible
    </div>
  );

  const { itineraire: allDays = [], resume, point_fort, incontournables = [] } = result;
  const itineraire = allDays.filter((d) => !d.locked);
  const lockedLabels = allDays.filter((d) => d.locked).map((d) => d.jour);

  return (
    <div className="space-y-6">
      {/* Progress Bar */}
      {trip?.date_depart && trip?.date_retour && (
        <TripProgressBar dateDepart={trip.date_depart} dateRetour={trip.date_retour} />
      )}

      {resume && (
        <div className="card-surface">
          <p className="text-[#5C554E] leading-relaxed italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>{resume}</p>
        </div>
      )}
      {point_fort && (
        <div className="bg-[#FDF8F3] border-l-4 border-[#C17F59] p-4 rounded-sm">
          <div className="flex items-start gap-3">
            <Star size={16} strokeWidth={1.5} className="text-[#C17F59] mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs uppercase tracking-widest text-[#8B5E34] mb-1" style={{ fontFamily: "'Lora', Georgia, serif" }}>À ne pas manquer</p>
              <p className="text-sm text-[#5C554E] leading-relaxed" style={{ fontFamily: "'Lora', Georgia, serif" }}>{point_fort}</p>
            </div>
          </div>
        </div>
      )}
      {incontournables?.length > 0 && (
        <div className="card-surface">
          <h3 className="text-xl mb-3" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}>Incontournables</h3>
          <ul className="space-y-2">
            {incontournables.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-[#5C554E]" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                <ChevronRight size={13} strokeWidth={2} className="text-[#C17F59] mt-0.5 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}
      {itineraire?.length > 0 && (
        <div>
          <h3 className="text-2xl mb-5" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}>
            Itinéraire jour par jour — {allDays.length} jours
          </h3>
          <div className="space-y-5 relative">
            <div className="absolute left-5 top-0 bottom-0 w-px bg-[#DCD4C6]" />
            {itineraire.map((day, i) => {
              const isToday = currentDayIndex === i;
              return (
                <div
                  key={i}
                  id={isToday ? "today-day" : undefined}
                  className="pl-14 relative animate-fade-in-up"
                  style={{ animationDelay: `${i * 40}ms` }}
                  data-testid={`day-card-${i}`}
                >
                  {/* Day number circle — gold if today */}
                  <div
                    className="absolute left-0 top-0 w-10 h-10 flex items-center justify-center text-sm transition-all"
                    style={{
                      fontFamily: "'Cormorant Garamond', Georgia, serif",
                      fontWeight: 600,
                      borderRadius: "50%",
                      background: isToday ? "#2B2623" : "#C17F59",
                      color: "#F9F6F0",
                      boxShadow: isToday ? "0 0 0 3px #C17F59, 0 0 0 5px #F9F6F0" : "none",
                    }}
                  >
                    {i + 1}
                  </div>

                  {/* Card — highlighted border if today */}
                  <div
                    className="card-surface transition-all"
                    style={isToday ? { borderLeft: "4px solid #C17F59", background: "#FDF8F3" } : {}}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="text-xs uppercase tracking-widest text-[#8B5E34]" style={{ fontFamily: "'Lora', Georgia, serif" }}>{day.jour}</p>
                      {isToday && (
                        <span
                          className="text-xs px-2 py-0.5 bg-[#C17F59] text-[#F9F6F0] flex-shrink-0"
                          style={{ fontFamily: "'Lora', Georgia, serif", borderRadius: "1px" }}
                          data-testid="today-badge"
                        >
                          Aujourd'hui
                        </span>
                      )}
                    </div>
                    <h4 className="text-xl mb-2" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}>{day.titre}</h4>
                    <p className="text-sm text-[#5C554E] leading-relaxed mb-4" style={{ fontFamily: "'Lora', Georgia, serif" }}>{day.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {day.repas_suggeres && (
                        <span className="inline-flex items-center gap-1.5 bg-[#FDF8F3] border border-[#DCD4C6] text-[#8B5E34] text-xs px-3 py-1.5" style={{ fontFamily: "'Lora', Georgia, serif", borderRadius: "1px" }}>
                          <Utensils size={10} strokeWidth={1.5} /> {day.repas_suggeres}
                        </span>
                      )}
                      {day.hebergement_suggere && (
                        <span className="inline-flex items-center gap-1.5 bg-[#F0FBF4] border border-[#BBF7D0] text-emerald-800 text-xs px-3 py-1.5" style={{ fontFamily: "'Lora', Georgia, serif", borderRadius: "1px" }}>
                          <Hotel size={10} strokeWidth={1.5} /> {day.hebergement_suggere}
                        </span>
                      )}
                      {day.budget_jour && (
                        <span className="inline-flex items-center gap-1.5 bg-[#F9F6F0] border border-[#DCD4C6] text-[#5C554E] text-xs px-3 py-1.5" style={{ fontFamily: "'Lora', Georgia, serif", borderRadius: "1px" }}>
                          {day.budget_jour}
                        </span>
                      )}
                    </div>
                    {/* Photo spots — matched dynamically from day title & description */}
                    <PhotoSpotCard
                      spots={day.spots_photo || []}
                      destination={trip?.destination}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <LockedDays labels={lockedLabels} onUnlock={onUnlock} />
        </div>
      )}

      {/* Emergency block — bottom of itinerary */}
      <EmergencyBlock urgences={result?.urgences} destination={trip?.destination || ""} />
    </div>
  );
}
